import { DotsSixVertical, Trash, Plus, X } from '@phosphor-icons/react';

import type { CreateFormFieldPayload, FieldType } from '@/types/form';

import styles from './FormFieldEditor.module.css';

const FIELD_TYPE_OPTIONS: { label: string; value: FieldType }[] = [
    { label: 'Texto corto', value: 'short_text' },
    { label: 'Texto largo', value: 'long_text' },
    { label: 'Opcion unica', value: 'single_choice' },
    { label: 'Opcion multiple', value: 'multiple_choice' },
    { label: 'Desplegable', value: 'dropdown' },
    { label: 'Escala lineal', value: 'linear_scale' },
    { label: 'Fecha', value: 'date' },
    { label: 'Numero', value: 'number' },
    { label: 'Hora', value: 'time' },
    { label: 'Archivo', value: 'file_upload' },
    { label: 'Grilla opcion unica', value: 'choice_grid' },
    { label: 'Grilla opcion multiple', value: 'checkbox_grid' },
];

interface FormFieldEditorProps {
    field: CreateFormFieldPayload;
    index: number;
    onChange: (index: number, field: CreateFormFieldPayload) => void;
    onDelete: (index: number) => void;
    canDelete: boolean;
    isQuizMode?: boolean;
}

function getChoices(field: CreateFormFieldPayload): string[] {
    const raw = field.config['choices'];
    if (Array.isArray(raw)) return raw as string[];
    return [];
}

function getGridRows(field: CreateFormFieldPayload): string[] {
    const raw = field.config['rows'];
    if (Array.isArray(raw)) return raw as string[];
    return ['Fila 1', 'Fila 2'];
}

function getGridColumns(field: CreateFormFieldPayload): string[] {
    const raw = field.config['columns'];
    if (Array.isArray(raw)) return raw as string[];
    return ['Columna 1', 'Columna 2', 'Columna 3'];
}

function isChoiceType(type: FieldType): boolean {
    return ['single_choice', 'multiple_choice', 'dropdown'].includes(type);
}

function isScaleType(type: FieldType): boolean {
    return type === 'linear_scale';
}

function isNumberType(type: FieldType): boolean {
    return type === 'number';
}

function isGridType(type: FieldType): boolean {
    return type === 'choice_grid' || type === 'checkbox_grid';
}

function isFileType(type: FieldType): boolean {
    return type === 'file_upload';
}

export function FormFieldEditor({ field, index, onChange, onDelete, canDelete, isQuizMode }: FormFieldEditorProps) {
    const update = (partial: Partial<CreateFormFieldPayload>) => {
        onChange(index, { ...field, ...partial });
    };

    const updateConfig = (partial: Record<string, unknown>) => {
        update({ config: { ...field.config, ...partial } });
    };

    const handleTypeChange = (newType: FieldType) => {
        let config: Record<string, unknown> = {};
        if (isChoiceType(newType)) {
            config = { choices: ['', ''] };
        } else if (newType === 'linear_scale') {
            config = { min: 1, max: 5, min_label: '', max_label: '' };
        } else if (isGridType(newType)) {
            config = { rows: ['Fila 1', 'Fila 2'], columns: ['Columna 1', 'Columna 2', 'Columna 3'] };
        } else if (newType === 'file_upload') {
            config = { max_size_mb: 10, allowed_types: ['image/*', 'application/pdf'] };
        }
        onChange(index, { ...field, field_type: newType, config });
    };

    const choices = getChoices(field);
    const gridRows = getGridRows(field);
    const gridColumns = getGridColumns(field);

    const addChoice = () => {
        updateConfig({ choices: [...choices, ''] });
    };

    const updateChoice = (ci: number, text: string) => {
        const next = choices.map((c, i) => (i === ci ? text : c));
        updateConfig({ choices: next });
    };

    const removeChoice = (ci: number) => {
        const next = choices.filter((_, i) => i !== ci);
        updateConfig({ choices: next });
    };

    const updateGridRow = (ri: number, text: string) => {
        const next = gridRows.map((r, i) => (i === ri ? text : r));
        updateConfig({ rows: next });
    };

    const addGridRow = () => {
        updateConfig({ rows: [...gridRows, `Fila ${gridRows.length + 1}`] });
    };

    const removeGridRow = (ri: number) => {
        if (gridRows.length <= 1) return;
        updateConfig({ rows: gridRows.filter((_, i) => i !== ri) });
    };

    const updateGridColumn = (ci: number, text: string) => {
        const next = gridColumns.map((c, i) => (i === ci ? text : c));
        updateConfig({ columns: next });
    };

    const addGridColumn = () => {
        updateConfig({ columns: [...gridColumns, `Columna ${gridColumns.length + 1}`] });
    };

    const removeGridColumn = (ci: number) => {
        if (gridColumns.length <= 1) return;
        updateConfig({ columns: gridColumns.filter((_, i) => i !== ci) });
    };

    return (
        <div className={styles.editor}>
            <div className={styles.dragHandle} aria-hidden="true">
                <DotsSixVertical size={18} />
            </div>

            <div className={styles.body}>
                {/* Label */}
                <input
                    className={styles.labelInput}
                    value={field.label}
                    onChange={(e) => update({ label: e.target.value })}
                    placeholder={`Pregunta ${index + 1}`}
                    maxLength={300}
                />

                {/* Description */}
                <input
                    className={styles.descriptionInput}
                    value={field.description ?? ''}
                    onChange={(e) => update({ description: e.target.value })}
                    placeholder="Descripcion (opcional)"
                    maxLength={500}
                />

                {/* Type + Required row */}
                <div className={styles.typeRow}>
                    <select
                        className={styles.typeSelect}
                        value={field.field_type}
                        onChange={(e) => handleTypeChange(e.target.value as FieldType)}
                    >
                        {FIELD_TYPE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>

                    <label className={styles.requiredToggle}>
                        <input
                            type="checkbox"
                            checked={field.is_required}
                            onChange={(e) => update({ is_required: e.target.checked })}
                            className={styles.hiddenCheckbox}
                        />
                        <span className={`${styles.toggleTrack} ${field.is_required ? styles.toggleActive : ''}`} />
                        <span className={styles.toggleLabel}>Obligatorio</span>
                    </label>
                </div>

                {/* Choices config */}
                {isChoiceType(field.field_type) && (
                    <div className={styles.choicesEditor}>
                        {choices.map((choice, ci) => (
                            <div key={ci} className={styles.choiceRow}>
                                <input
                                    className={styles.choiceInput}
                                    value={choice}
                                    onChange={(e) => updateChoice(ci, e.target.value)}
                                    placeholder={`Opcion ${ci + 1}`}
                                    maxLength={200}
                                />
                                {choices.length > 2 && (
                                    <button
                                        type="button"
                                        className={styles.removeChoiceBtn}
                                        onClick={() => removeChoice(ci)}
                                        aria-label="Eliminar opcion"
                                    >
                                        <X size={14} />
                                    </button>
                                )}
                            </div>
                        ))}
                        {choices.length < 10 && (
                            <button
                                type="button"
                                className={styles.addChoiceBtn}
                                onClick={addChoice}
                            >
                                <Plus size={14} />
                                Agregar opcion
                            </button>
                        )}

                        {/* Shuffle choices toggle */}
                        <label className={styles.shuffleToggle}>
                            <input
                                type="checkbox"
                                checked={field.shuffle_choices ?? false}
                                onChange={(e) => update({ shuffle_choices: e.target.checked })}
                                className={styles.hiddenCheckbox}
                            />
                            <span className={`${styles.toggleTrack} ${field.shuffle_choices ? styles.toggleActive : ''}`} />
                            <span className={styles.toggleLabel}>Mezclar opciones</span>
                        </label>
                    </div>
                )}

                {/* Scale config */}
                {isScaleType(field.field_type) && (
                    <div className={styles.scaleConfig}>
                        <div className={styles.scaleRow}>
                            <label className={styles.configLabel}>Min</label>
                            <input
                                type="number"
                                className={styles.configInput}
                                value={Number(field.config['min'] ?? 1)}
                                onChange={(e) => updateConfig({ min: parseInt(e.target.value, 10) })}
                                min={0}
                                max={10}
                            />
                            <label className={styles.configLabel}>Max</label>
                            <input
                                type="number"
                                className={styles.configInput}
                                value={Number(field.config['max'] ?? 5)}
                                onChange={(e) => updateConfig({ max: parseInt(e.target.value, 10) })}
                                min={2}
                                max={10}
                            />
                        </div>
                        <div className={styles.scaleRow}>
                            <input
                                className={styles.scaleLabelInput}
                                value={String(field.config['min_label'] ?? '')}
                                onChange={(e) => updateConfig({ min_label: e.target.value })}
                                placeholder="Etiqueta minimo (ej. Malo)"
                                maxLength={50}
                            />
                            <input
                                className={styles.scaleLabelInput}
                                value={String(field.config['max_label'] ?? '')}
                                onChange={(e) => updateConfig({ max_label: e.target.value })}
                                placeholder="Etiqueta maximo (ej. Excelente)"
                                maxLength={50}
                            />
                        </div>
                    </div>
                )}

                {/* Number config */}
                {isNumberType(field.field_type) && (
                    <div className={styles.scaleRow}>
                        <label className={styles.configLabel}>Min</label>
                        <input
                            type="number"
                            className={styles.configInput}
                            value={field.config['min'] !== undefined ? Number(field.config['min']) : ''}
                            onChange={(e) => updateConfig({ min: e.target.value ? parseInt(e.target.value, 10) : undefined })}
                            placeholder="—"
                        />
                        <label className={styles.configLabel}>Max</label>
                        <input
                            type="number"
                            className={styles.configInput}
                            value={field.config['max'] !== undefined ? Number(field.config['max']) : ''}
                            onChange={(e) => updateConfig({ max: e.target.value ? parseInt(e.target.value, 10) : undefined })}
                            placeholder="—"
                        />
                    </div>
                )}

                {/* Grid config */}
                {isGridType(field.field_type) && (
                    <div className={styles.gridConfig}>
                        <div className={styles.gridSection}>
                            <span className={styles.gridSectionLabel}>Filas</span>
                            {gridRows.map((row, ri) => (
                                <div key={ri} className={styles.choiceRow}>
                                    <input
                                        className={styles.choiceInput}
                                        value={row}
                                        onChange={(e) => updateGridRow(ri, e.target.value)}
                                        placeholder={`Fila ${ri + 1}`}
                                        maxLength={100}
                                    />
                                    {gridRows.length > 1 && (
                                        <button
                                            type="button"
                                            className={styles.removeChoiceBtn}
                                            onClick={() => removeGridRow(ri)}
                                            aria-label="Eliminar fila"
                                        >
                                            <X size={14} />
                                        </button>
                                    )}
                                </div>
                            ))}
                            <button
                                type="button"
                                className={styles.addChoiceBtn}
                                onClick={addGridRow}
                            >
                                <Plus size={14} />
                                Agregar fila
                            </button>
                        </div>
                        <div className={styles.gridSection}>
                            <span className={styles.gridSectionLabel}>Columnas</span>
                            {gridColumns.map((col, ci) => (
                                <div key={ci} className={styles.choiceRow}>
                                    <input
                                        className={styles.choiceInput}
                                        value={col}
                                        onChange={(e) => updateGridColumn(ci, e.target.value)}
                                        placeholder={`Columna ${ci + 1}`}
                                        maxLength={100}
                                    />
                                    {gridColumns.length > 1 && (
                                        <button
                                            type="button"
                                            className={styles.removeChoiceBtn}
                                            onClick={() => removeGridColumn(ci)}
                                            aria-label="Eliminar columna"
                                        >
                                            <X size={14} />
                                        </button>
                                    )}
                                </div>
                            ))}
                            <button
                                type="button"
                                className={styles.addChoiceBtn}
                                onClick={addGridColumn}
                            >
                                <Plus size={14} />
                                Agregar columna
                            </button>
                        </div>
                    </div>
                )}

                {/* File upload config */}
                {isFileType(field.field_type) && (
                    <div className={styles.scaleRow}>
                        <label className={styles.configLabel}>Max MB</label>
                        <input
                            type="number"
                            className={styles.configInput}
                            value={Number(field.config['max_size_mb'] ?? 10)}
                            onChange={(e) => updateConfig({ max_size_mb: parseInt(e.target.value, 10) })}
                            min={1}
                            max={100}
                        />
                    </div>
                )}

                {/* Quiz: points input */}
                {isQuizMode && (
                    <div className={styles.scaleRow}>
                        <label className={styles.configLabel}>Puntos</label>
                        <input
                            type="number"
                            className={styles.configInput}
                            value={field.points ?? 0}
                            onChange={(e) => update({ points: parseInt(e.target.value, 10) || 0 })}
                            min={0}
                            max={100}
                        />
                    </div>
                )}
            </div>

            {/* Delete */}
            {canDelete && (
                <button
                    type="button"
                    className={styles.deleteBtn}
                    onClick={() => onDelete(index)}
                    aria-label="Eliminar campo"
                >
                    <Trash size={16} />
                </button>
            )}
        </div>
    );
}
